import { Auth0Client, createAuth0Client, User as Auth0User } from "@auth0/auth0-spa-js";
import wretch from "wretch";

export type User = {
  id: string;
  email: string;
  email_verified: boolean;
  name: string;
  isFederated: boolean;
};

function userFromAuthUser(authUser: Auth0User | undefined): User | undefined {
  if (!authUser) {
    return undefined;
  }
  return {
    id: authUser?.sub ?? "",
    name: authUser?.name ?? "",
    email: authUser?.email ?? "",
    email_verified: authUser?.email_verified ?? false,
    isFederated: authUser?.sub?.startsWith("oauth2|proAlpha|") ?? false,
  };
}

export type AuthProvider = {
  init(credentials: Auth0Credentials): void;

  isAuthenticated(): Promise<boolean>;

  user(): Promise<User | undefined>;

  getTokenSilently(): Promise<string>;

  signin({ returnTo }: { returnTo: string }): Promise<string>;

  handleSigninRedirect(): Promise<void>;

  signout(): Promise<void>;

  refresh(): Promise<void>;

  requestPasswortChange(): Promise<void>;
};

export type Auth0Credentials = Readonly<{
  auth0_domain: string;
  auth0_client_id: string;
  auth0_audience: string;
}>;

let auth0Credentials: Auth0Credentials | undefined = undefined;
let auth0ClientPromise: ReturnType<typeof createAuth0Client> | undefined = undefined;

async function getClient(): Promise<Auth0Client> {
  if (auth0Credentials == null) {
    throw new Error("Auth0 configuration not set");
  }
  if (!auth0ClientPromise) {
    auth0ClientPromise = createAuth0Client({
      domain: auth0Credentials.auth0_domain,
      clientId: auth0Credentials.auth0_client_id,
      authorizationParams: {
        audience: auth0Credentials.auth0_audience,
      },
    });
  }
  return auth0ClientPromise;
}

export const AuthService: AuthProvider = {
  init({ auth0_domain, auth0_client_id, auth0_audience }: Auth0Credentials): void {
    auth0Credentials = {
      auth0_domain,
      auth0_client_id,
      auth0_audience,
    };
  },

  async isAuthenticated() {
    const client = await getClient();
    return client.isAuthenticated();
  },

  async user() {
    const client = await getClient();
    const user = await client.getUser();
    return userFromAuthUser(user);
  },

  async signin({ returnTo }: { returnTo: string }) {
    const client = await getClient();
    return new Promise((resolve) => {
      client.loginWithRedirect({
        authorizationParams: {
          redirect_uri: window.location.origin,
        },
        appState: { returnTo },
        openUrl: async (url) => {
          resolve(url);
        },
      });
    });
  },

  async handleSigninRedirect() {
    const query = window.location.search;
    if (query.includes("code=") && query.includes("state=")) {
      const client = await getClient();
      const { appState } = await client.handleRedirectCallback();
      window.location.replace(appState?.returnTo ?? "/");
    }
  },

  async signout() {
    const client = await getClient();
    await client.logout({ logoutParams: { returnTo: window.location.origin } });
  },

  async getTokenSilently(): Promise<string> {
    const client = await getClient();
    // @auth0/auth0-spa-js@2.25.0 changed the return type to string|undefined, but we
    // cannot deal with the cases where it can return undefined anywy
    return (await client.getTokenSilently())!;
  },

  async refresh(): Promise<void> {
    const client = await getClient();
    await client.getTokenSilently({ cacheMode: "off" });
  },

  async requestPasswortChange() {
    if (auth0Credentials == null) {
      throw new Error("Auth0 configuration not set");
    }
    const user = await AuthService.user();
    if (!user) {
      throw new Error("User not logged in");
    }
    await wretch()
      .url(`https://${auth0Credentials.auth0_domain}/dbconnections/change_password`)
      .post({
        client_id: auth0Credentials.auth0_client_id,
        email: user.email,
        connection: "Username-Password-Authentication",
      })
      .res();
  },
};
